import React, { Component } from 'react';
import Meanings from './Meanings';
import { cx } from '@emotion/css';
import * as styles from './questionWithTextFeild.style';
import QuizIndicator from './QuizIndicator';
import withQuizPic from './hoc/withQuizPic';
import MathParser from './MathParser';
import 'mathlive';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// Ensure KaTeX is on window for Quill Formula module
if (typeof window !== 'undefined') {
  window.katex = katex;
}

// Helper to convert $latex$ to Quill HTML formula spans
function convertLatexToQuillHtml(text) {
  if (!text) return '';
  let html = '';
  let i = 0;
  while (i < text.length) {
    if (text[i] !== '$') {
      const char = text[i++];
      if (char === '<') html += '&lt;';
      else if (char === '>') html += '&gt;';
      else if (char === '&') html += '&amp;';
      else html += char;
      continue;
    }
    
    // Check for block math $$...$$
    if (i + 1 < text.length && text[i + 1] === '$') {
      const closeIdx = text.indexOf('$$', i + 2);
      if (closeIdx !== -1) {
        const content = text.substring(i + 2, closeIdx);
        const escapedContent = content.replace(/"/g, '&quot;');
        html += `<span class="ql-formula" data-value="${escapedContent}"></span>`;
        i = closeIdx + 2;
        continue;
      }
    }

    // Check for inline math $...$
    let j = i + 1;
    while (j < text.length && text[j] !== '$') {
      if (text[j] === '\\') j++;
      j++;
    }
    if (j < text.length) {
      const content = text.substring(i + 1, j);
      const hasLatex = /[\\^_{}]/.test(content);
      let isMath = hasLatex;
      if (!hasLatex) {
        const validSpacing = !/^\s/.test(content) && !/\s$/.test(content);
        const noLongWords = !/[a-zA-Z]{3,}/.test(content);
        if (validSpacing && noLongWords && content.length > 0) isMath = true;
      }
      if (isMath) {
        const escapedContent = content.replace(/"/g, '&quot;');
        html += `<span class="ql-formula" data-value="${escapedContent}"></span>`;
        i = j + 1;
        continue;
      }
    }
    
    html += '$';
    i++;
  }
  return html;
}

class QuestionswithTextField extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: props.selectedanswer || '',
      valueHtml: convertLatexToQuillHtml(props.selectedanswer || ''),
      quizPic: '',
      showmeaning: false,
      meaning: '',
      showBuilder: false,
      mathBuilderValue: '',
    };

    this.textInput = React.createRef();
    this.mathFieldRef = React.createRef();
    this.handleKeypress = this.handleKeypress.bind(this);
    this.closemeaningwindow = this.closemeaningwindow.bind(this);

    this.quillModules = {
      toolbar: false,
      formula: true,
      keyboard: {
        bindings: {
          enter: {
            key: 13,
            handler: () => {
              this.props.nextbutton();
            }
          }
        }
      }
    };
  }

  getRawText(editor) {
    const delta = editor.getContents();
    let text = '';
    if (delta && delta.ops) {
      delta.ops.forEach(op => {
        if (typeof op.insert === 'string') {
          text += op.insert;
        } else if (op.insert && op.insert.formula) {
          text += `$${op.insert.formula}$`;
        }
      });
    }
    if (text.endsWith('\n')) {
      text = text.slice(0, -1);
    }
    return text;
  }

  focus() {
    if (this.mathFieldRef.current) {
      this.mathFieldRef.current.focus();
    }
  }

  closemeaningwindow() {
    this.setState({ showmeaning: false });
    this.focus();
  }

  handleKeypress(e) {
    if (e.charCode === 13) {
      this.props.nextbutton();
    }
  }

  handleChange = (content, delta, source, editor) => {
    const rawText = this.getRawText(editor);
    this.setState({ valueHtml: content, value: rawText }, () => {
      this.props.selected(rawText, '1');
    });
  };

  handleMathInput = (e) => {
    this.setState({ mathBuilderValue: e.target.value });
  };

  toggleBuilder = () => {
    if (this.state.showBuilder) {
      this.setState({ showBuilder: false }, () => {
        if (this.mathFieldRef.current) {
          this.mathFieldRef.current.removeEventListener('input', this.handleMathInput);
        }
      });
    } else {
      this.setState({ showBuilder: true, mathBuilderValue: '' }, () => {
        if (this.mathFieldRef.current) {
          this.mathFieldRef.current.addEventListener('input', this.handleMathInput);
          this.mathFieldRef.current.focus();
        }
      });
    }
  };

  insertMath = () => {
    if (!this.textInput.current) return;
    
    const editor = this.textInput.current.getEditor();
    const range = editor.getSelection(true);
    const index = range ? range.index : editor.getLength();
    
    editor.insertEmbed(index, 'formula', this.state.mathBuilderValue);
    editor.setSelection(index + 1);

    this.setState({ showBuilder: false }, () => {
      if (this.mathFieldRef.current) {
        this.mathFieldRef.current.removeEventListener('input', this.handleMathInput);
      }
    });
  };

  selectedanswer = (answer, id) => {
    this.props.selected(answer, id);
  };

  componentDidMount() {
    this.setState({
      quizPic: this.props.getRandomImage(this.props.questionnumber),
    });
  }

  componentWillUnmount() {
    if (this.mathFieldRef.current) {
      this.mathFieldRef.current.removeEventListener('input', this.handleMathInput);
    }
  }

  render() {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <h2 className={cx(styles.title)}>
          <span>{this.props.questionnumber}/</span>
          {this.props.questionCount}
        </h2>
     
        <img className={cx(styles.img)} src={this.state.quizPic} alt="" />
        <QuizIndicator
          questionCount={this.props.questionCount}
          questionnumber={this.props.questionnumber}
        />
        {this.state.showmeaning && (
          <Meanings
            closewindow={this.closemeaningwindow}
            meaning={this.state.meaning}
          />
        )}

        <div className="">
          <div className={cx(styles.questions)}>
            <MathParser text={this.props.question} />
          </div>
          <div className={cx(styles.note)}>{this.props.note}</div>

          <div className={cx(styles.answers)}>
            <div className="s-title">Type Answer below</div>
            
            <div style={{ position: 'relative', marginTop: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ flex: 1, backgroundColor: '#fff', borderRadius: '5px', border: '1px solid #ccc' }}>
                  <ReactQuill
                    ref={this.textInput}
                    theme="snow"
                    value={this.state.valueHtml}
                    onChange={this.handleChange}
                    modules={this.quillModules}
                    placeholder="Type your answer here..."
                    style={{ backgroundColor: '#fff', borderRadius: '5px', border: 'none' }}
                  />
                </div>
                <div style={{ display: 'inline-block', marginLeft: '10px' }}>
                  <button 
                    type="button"
                    onClick={this.toggleBuilder}
                    title="Math Equation Builder"
                    style={{ 
                      background: '#f0f0f0', 
                      border: '1px solid #ccc', 
                      borderRadius: '5px', 
                      padding: '8px 12px', 
                      cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>⌨️</span>
                  </button>
                </div>
              </div>
              
              {this.state.showBuilder && (
                <div 
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    zIndex: 1000,
                    backgroundColor: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                    width: '100%',
                    marginTop: '8px'
                  }}
                >
                  <div style={{ marginBottom: '8px', fontWeight: '600', fontSize: '14px', color: '#475569', display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '6px' }}>⌨️</span> Math Equation Builder
                  </div>
                  <math-field
                    ref={this.mathFieldRef}
                    style={{ 
                      width: '100%',
                      fontSize: '1.2rem',
                      padding: '8px',
                      backgroundColor: '#f8fafc',
                      border: '1px solid #cbd5e1',
                      borderRadius: '6px',
                      color: '#0f172a',
                      outline: 'none'
                    }}
                  >
                    {this.state.mathBuilderValue}
                  </math-field>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px', gap: '8px' }}>
                    <button 
                      type="button"
                      onClick={this.toggleBuilder}
                      style={{ padding: '6px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', color: '#64748b', cursor: 'pointer', fontSize: '13px', fontWeight: '500' }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="button"
                      onClick={this.insertMath}
                      style={{ padding: '6px 14px', borderRadius: '6px', border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: '13px', fontWeight: '600', boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)' }}
                    >
                      Insert into Answer
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default withQuizPic(QuestionswithTextField);
